import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Sparkles } from 'lucide-react';
import { api } from '../api/client';
import type { OnboardingHire, OnboardingStatus } from '@pa/shared';

const STATUS_COLUMNS: { key: OnboardingStatus; label: string }[] = [
  { key: 'PRE_BOARDING', label: 'Pre-boarding' },
  { key: 'ONE_MONTH', label: '1-month' },
  { key: 'THREE_MONTH', label: '3-month' },
  { key: 'SIX_MONTH', label: '6-month' },
  { key: 'PRE_OFFBOARDING', label: 'Pre-offboarding' },
  { key: 'OFFBOARDING', label: 'Offboarding' },
];

export default function OnboardingView() {
  const qc = useQueryClient();
  const hires = useQuery({ queryKey: ['hires'], queryFn: () => api.get<OnboardingHire[]>('/api/hires') });
  const [showForm, setShowForm] = useState(false);

  const create = useMutation({
    mutationFn: (data: Partial<OnboardingHire>) => api.post<OnboardingHire>('/api/hires', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hires'] });
      setShowForm(false);
    },
  });

  return (
    <div className="max-w-7xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Flows</h1>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="inline-flex items-center gap-2 bg-[#6344C6] hover:bg-[#5a3eb8] text-white rounded-xl px-4 py-2.5 text-sm font-medium"
        >
          <Plus size={16} /> Add hire
        </button>
      </div>

      {showForm && (
        <HireForm
          onSubmit={(d) => create.mutate(d)}
          submitting={create.isPending}
        />
      )}

      {hires.data && hires.data.length === 0 && (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-12 text-center text-slate-500">
          <Sparkles className="mx-auto mb-3 text-slate-300" size={40} />
          <p className="font-medium">No flows yet</p>
          <p className="text-sm">Start an onboarding flow by adding a hire.</p>
        </div>
      )}

      {hires.data && hires.data.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {STATUS_COLUMNS.map((col) => {
            const items = hires.data!.filter((h) => h.status === col.key);
            return (
              <div key={col.key} className="bg-slate-50 rounded-2xl p-4">
                <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">{col.label}</p>
                <div className="space-y-2">
                  {items.length === 0 && <p className="text-xs text-slate-400">—</p>}
                  {items.map((h) => (
                    <div key={h.id} className="bg-white rounded-xl p-3 border border-slate-100 shadow-sm">
                      <p className="font-medium text-sm truncate">{h.name}</p>
                      <p className="text-xs text-slate-500 truncate">{h.role}</p>
                      {h.stage && <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6344C6] mt-2">{h.stage}</p>}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function HireForm({
  onSubmit,
  submitting,
}: {
  onSubmit: (d: Partial<OnboardingHire>) => void;
  submitting: boolean;
}) {
  const [data, setData] = useState({
    name: '',
    role: '',
    status: 'PRE_BOARDING' as OnboardingStatus,
    stage: 'Contract signature',
    startDate: new Date().toISOString().split('T')[0],
    visaRequired: false,
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(data);
      }}
      className="bg-white border border-slate-100 rounded-2xl p-6 mb-6 grid grid-cols-2 gap-4"
    >
      <Field label="Name"><input required value={data.name} onChange={(e) => setData({ ...data, name: e.target.value })} className={inputCls} /></Field>
      <Field label="Role"><input required value={data.role} onChange={(e) => setData({ ...data, role: e.target.value })} className={inputCls} /></Field>
      <Field label="Status">
        <select value={data.status} onChange={(e) => setData({ ...data, status: e.target.value as OnboardingStatus })} className={inputCls}>
          {STATUS_COLUMNS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </Field>
      <Field label="Stage"><input value={data.stage} onChange={(e) => setData({ ...data, stage: e.target.value })} className={inputCls} /></Field>
      <Field label="Start date"><input type="date" value={data.startDate} onChange={(e) => setData({ ...data, startDate: e.target.value })} className={inputCls} /></Field>
      <label className="flex items-end gap-2 text-sm text-slate-700 pb-2">
        <input type="checkbox" checked={data.visaRequired} onChange={(e) => setData({ ...data, visaRequired: e.target.checked })} /> Visa required
      </label>
      <div className="col-span-2 flex justify-end">
        <button disabled={submitting} className="bg-[#6344C6] hover:bg-[#5a3eb8] disabled:opacity-50 text-white rounded-xl px-4 py-2.5 text-sm font-medium">
          {submitting ? 'Saving…' : 'Save hire'}
        </button>
      </div>
    </form>
  );
}

const inputCls = 'w-full border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#6344C6]/40';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1 block">{label}</label>
      {children}
    </div>
  );
}
